import { useIsFocused } from "@react-navigation/native";
import { Box } from "arkh";
import React, { useState, useEffect, useLayoutEffect } from "react";
import { useWorkspaceId, useWorkspacePermissions } from "../../workspaceId";
import { WorkspaceRoutineOccasionsHistory } from "./WorkspaceRoutineOccasionsHistory";
import { WorkspaceRoutineOccasionStoresList } from "./WorkspaceRoutineOccasionStoresList";
import { graphql, useLazyLoadQuery } from "react-relay";
import { WorkspaceRoutineOccasionStatusTabScreenQuery } from "./__generated__/WorkspaceRoutineOccasionStatusTabScreenQuery.graphql";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";

import { WorkspaceDetailHeader } from "../../WorkspaceDetailHeader";

type StatusTabProps = {
  onPressListItem(
    routineId: string,
    storeId: string,
    storeName: string,
    storeTimezone: string,
    sequenceNumber: number
  ): void;
  setSequenceNumber: (sequenceNumber: number) => void;
  routineId: string;
  sequenceNumber: number;
  setHasAdmin: (hasAdmin: boolean) => void;
};

export function WorkspaceRoutineOccasionStatusTabScreen(props: StatusTabProps) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => (
        <WorkspaceRoutineOccasionStatusTabComponent
          fetchKey={fetchKey}
          {...props}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}
function WorkspaceRoutineOccasionStatusTabComponent(
  props: StatusTabProps & { fetchKey: string }
) {
  const workspaceId = useWorkspaceId();
  const isFocused = useIsFocused();
  const [historyOpen, setHistoryOpen] = useState(false);

  const data = useLazyLoadQuery<WorkspaceRoutineOccasionStatusTabScreenQuery>(
    graphql`
      query WorkspaceRoutineOccasionStatusTabScreenQuery(
        $routineId: ID!
        $sequenceNumber: Int!
        $workspaceId: ID!
      ) {
        routine(id: $routineId) {
          id
          title
          isArchived
          isWorkspaceOwner(workspaceId: $workspaceId)
          workspaceOccasion(
            sequenceNumber: $sequenceNumber
            workspaceId: $workspaceId
          ) {
            id
            sequenceNumber
            ...WorkspaceRoutineOccasionStoresList_routineWorkspaceOccasion
          }
        }
      }
    `,
    {
      routineId: props.routineId,
      sequenceNumber: props.sequenceNumber * 1,
      workspaceId,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  useEffect(() => {
    if (!isFocused && historyOpen) {
      setHistoryOpen(false);
    }
  }, [historyOpen, isFocused]);

  const routine = data.routine;
  const wo = data.routine?.workspaceOccasion;

  const permission = useWorkspacePermissions();
  const hasAdmin = (routine?.isWorkspaceOwner && permission.write) ?? false;

  const setHasAdmin = props.setHasAdmin;
  useLayoutEffect(() => {
    setHasAdmin(hasAdmin);
  }, [hasAdmin, setHasAdmin]);

  if (!wo) {
    return null;
  }
  if (!routine) {
    return null;
  }
  return (
    <>
      <Box
        spaceTop="medium"
        style={{ flex: 1 }}
        color="surface-background-light"
      >
        <Box
          spaceHorizontal="medium"
          style={{
            maxWidth: 900,
            width: "100%",
            alignSelf: "center",
          }}
          spaceTop="medium"
        >
          <WorkspaceDetailHeader
            color="surface-background-light"
            spaceBottom="none"
            spaceTop="none"
            title={routine.title}
          />
        </Box>

        <WorkspaceRoutineOccasionStoresList
          openOccasionsHistory={() => {
            setHistoryOpen(true);
          }}
          occasion={wo}
          onPress={props.onPressListItem}
        />
      </Box>

      <WorkspaceRoutineOccasionsHistory
        workspaceId={workspaceId}
        routineId={props.routineId}
        sequenceNumber={props.sequenceNumber}
        archivedRoutine={data.routine?.isArchived ?? false}
        onSelectOccasion={(sequenceNumber) => {
          setHistoryOpen(false);
          props.setSequenceNumber(sequenceNumber);
        }}
        isOpen={historyOpen}
        onClose={() => {
          setHistoryOpen(false);
        }}
      />
    </>
  );
}
